import React from 'react';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import { Accordion, Grid } from 'semantic-ui-react';
import fetch, { fetchConfig } from '@/hooks/fetch';
import Filter from '@/components/Filter';
import NoRows from '@/components/table/NoRows';
import RolePanelContainer, { mapDispatchToProps, mapStateToProps } from '../components/RolePanelContainer';
import RoleName from './RoleName';
import RoleDescription from './RoleDescription';
import {
  permissionCategories,
  tokenPermissionCategories,
  adminPermissions,
  datasetPermissions,
  requestPermissions,
  collectionPermissions,
  datasetTokenPermissions,
  requestTokenPermissions,
  adminTokenPermissions,
  roleGroupings,
  allPermissionCategories,
} from '../constants';
import PermissionsAccordion from './PermissionsAccordion';
import RoleSmartPermissions from './RoleSmartPermissions';
import RoleActionsList from '../roleactions/containers/RoleActionsList';
import RoleTokens from './RoleTokens';
import { filterPermissions } from '../helper';
import RoleErrorMessages from '../components/RoleErrorMessages';

const RolePanelFull = ({
  canRead,
  category,
  code,
  description,
  exists,
  fetchData,
  filter,
  grouping,
  isDirty,
  name,
  onChange,
  panels,
  readErrors,
  readStatus,
  role,
  updateErrors,
  updateFilter,
  updatePanels,
  updateProperties,
  url,
}) => {
  const intl = useIntl();
  fetch.useFetchByCode(fetchData, category, code, readStatus, fetchConfig([], () => !role));
  const filteredCategories = filter
    ? filterPermissions(allPermissionCategories, filter, grouping, intl) : allPermissionCategories;

  const leftSideUserPanels = [
    { title: 'permissions.datasets.title', categories: datasetPermissions },
    { title: 'permissions.requests.title', categories: requestPermissions },
    { title: 'permissions.cohort.title', categories: [permissionCategories.COHORTS] },
    { title: 'permissions.usergroups.title', categories: [permissionCategories.USER_GROUPS] },
  ];
  const rightSideUserPanels = [
    { title: 'permissions.admin.title', categories: adminPermissions },
    { title: 'permissions.collections.title', categories: collectionPermissions },
    { title: 'permissions.search.title', categories: [permissionCategories.SEARCH] },
    { title: 'permissions.tokens.title', categories: [permissionCategories.USERS_TOKEN] },
  ];

  const leftSideTokenPanels = [
    { title: 'permissions.datasets.title', categories: datasetTokenPermissions },
    { title: 'permissions.requests.title', categories: requestTokenPermissions },
    {
      title: 'permissions.cohort.title',
      categories: [tokenPermissionCategories.COHORTS],
    },
  ];
  const rightSideTokenPanels = [
    { title: 'permissions.admin.title', categories: adminTokenPermissions },
    { title: 'permissions.collections.title', categories: collectionPermissions },
    { title: 'permissions.search.title', categories: [tokenPermissionCategories.SEARCH] },
  ];

  return (
    <RolePanelContainer
      canRead={canRead}
      isDirty={isDirty}
      exists={exists}
      description={description}
      name={name}
      onChange={onChange}
      readErrors={readErrors}
      readStatus={readStatus}
      url={url}
    >
      <div className="aridhia-role-panel-full">
        <RoleErrorMessages
          category={category}
          code={code}
          updateErrors={updateErrors}
          updateProperties={updateProperties}
        />
        <Grid>
          <Grid.Row columns="equal">
            <Grid.Column>
              <RoleName code={code} category={category} />
            </Grid.Column>
            <Grid.Column>
              <div className="float-r">
                <Filter
                  className="aridhia-list-filter"
                  filterTerm={filter}
                  updateFilter={({ value }) => updateFilter(code, category, value)}
                  label={<RoleSmartPermissions code={code} category={category} />}
                />
              </div>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns="equal">
            <Grid.Column>
              <RoleDescription code={code} category={category} />
            </Grid.Column>
            <Grid.Column>
              {grouping === roleGroupings.USERS && (
                <>
                  <RoleTokens code={code} category={category} />
                  <RoleActionsList code={code} category={category} />
                </>
              )}
            </Grid.Column>
          </Grid.Row>
          {filteredCategories.length === 0 && (
            <Grid.Row columns="1">
              <Grid.Column>
                <NoRows icon="lock" entity="permission" />
              </Grid.Column>
            </Grid.Row>
          )}
          {grouping === roleGroupings.USERS && filteredCategories.length > 0 && (
          <Grid.Row columns="equal">
            <Grid.Column>
              <Accordion>
                {leftSideUserPanels.map((panel, index) => (
                  <PermissionsAccordion
                    key={`users_permissions_accordion_left_${panel.title}`}
                    title={panel.title}
                    active={panels.left.includes(index)}
                    index={index}
                    onClick={() => updatePanels(code, category, index, 'left')}
                    code={code}
                    category={category}
                    categories={panel.categories}
                    grouping={grouping}
                  />
                ))}
              </Accordion>
            </Grid.Column>
            <Grid.Column>
              <Accordion>
                {rightSideUserPanels.map((panel, index) => (
                  <PermissionsAccordion
                    key={`users_permissions_accordion_right_${panel.title}`}
                    title={panel.title}
                    active={panels.right.includes(index)}
                    index={index}
                    onClick={() => updatePanels(code, category, index, 'right')}
                    code={code}
                    category={category}
                    categories={panel.categories}
                    grouping={grouping}
                  />
                ))}
              </Accordion>
            </Grid.Column>
          </Grid.Row>
          )}
          {grouping === roleGroupings.TOKENS && filteredCategories.length > 0 && (
          <Grid.Row columns="equal">
            <Grid.Column>
              <Accordion>
                {leftSideTokenPanels.map((panel, index) => (
                  <PermissionsAccordion
                    key={`tokens_permissions_accordion_left_${panel.title}`}
                    title={panel.title}
                    active={panels.left.includes(index)}
                    index={0}
                    onClick={() => updatePanels(code, category, index, 'left')}
                    code={code}
                    category={category}
                    categories={panel.categories}
                    grouping={grouping}
                  />
                ))}
              </Accordion>
            </Grid.Column>
            <Grid.Column>
              <Accordion>
                {rightSideTokenPanels.map((panel, index) => (
                  <PermissionsAccordion
                    key={`tokens_permissions_accordion_right_${panel.title}`}
                    title={panel.title}
                    active={panels.right.includes(index)}
                    index={0}
                    onClick={() => updatePanels(code, category, index, 'right')}
                    code={code}
                    category={category}
                    categories={panel.categories}
                    grouping={grouping}
                  />
                ))}
              </Accordion>
            </Grid.Column>
          </Grid.Row>
          )}
        </Grid>
      </div>
    </RolePanelContainer>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(RolePanelFull);
